import { ConsumationType } from '@customTypes/types';
import { PlusCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Flex,
  RadioGroup,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { PageLoading } from '@/pages/PageLoading';
import { useWorkingConditionPage } from '@/pages/club/onboard/components/stages/working-conditions/hooks/useWorkingConditionsPage';
import { translate } from '@/shared/i18n/translate';
import { LBModalFullscreenLayout } from '@/shared/layouts';
import {
  LBBadge,
  LBButton,
  LBCheckbox,
  LBLinkButton,
  LBModalHeader,
  LBNumberInput,
  LBRadio,
  LBTextArea,
  LBTextInput
} from '@/shared/ui-kit/components';

export const WorkingConditions = () => {
  const {
    state: { isLoading, form, dressList },
    functions: { onSubmit, setDressList }
  } = useWorkingConditionPage();
  const { formatMessage } = useIntl();
  const [dressValue, setDressValue] = useState('');
  const [opened, { close, open }] = useDisclosure();

  if (isLoading) return <PageLoading />;

  return (
    <>
      <LBModalFullscreenLayout opened={opened} onClose={close}>
        <LBModalHeader onClose={close} />
        <Title>{translate('commons.dressCode')}</Title>
        <Space h={8} />
        <Text>
          {translate(
            'pages.onboarding.club.workConditions.popups.dressCode.description'
          )}
        </Text>
        <Space h={24} />
        <LBTextInput
          value={dressValue}
          placeholder={formatMessage({
            id: 'pages.onboarding.club.workConditions.popups.dressCode.enterDressCodeElement'
          })}
          rightSection={
            <ActionIcon
              variant='transparent'
              onClick={() => {
                if (dressValue.length) {
                  setDressList((prev) => [...prev, dressValue]);
                  setDressValue('');
                }
              }}
            >
              <PlusCircleIcon width={18} />
            </ActionIcon>
          }
          onChange={(e) => setDressValue(e.target.value)}
        />
        <Space h={16} />
        <Stack gap={16}>
          {dressList.map((item, index) => (
            <LBCheckbox
              key={item}
              label={item}
              defaultChecked={
                !!form
                  .getValues()
                  .dressCodes.filter((listItem) => listItem === item)[0]
              }
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  return form.insertListItem('dressCodes', item);
                }
                form.removeListItem('dressCodes', index);
              }}
            />
          ))}
        </Stack>
        <Space h={50} />
        <LBButton fullWidth onClick={close}>
          {translate('commons.save-and-continue')}
        </LBButton>
      </LBModalFullscreenLayout>

      <form onSubmit={onSubmit}>
        <Title order={1}>
          {translate('pages.onboarding.club.workConditions.title')}
        </Title>
        <Space h={32} />
        <Stack gap={24}>
          <Flex direction='column' gap={8}>
            <Text>{translate('commons.artistConsumation.label')}</Text>
            <RadioGroup
              key={form.key('consummationType')}
              {...form.getInputProps('consummationType')}
            >
              <Stack>
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.artistConsumation.Active')}
                  value={ConsumationType.Active}
                />
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.artistConsumation.Passive')}
                  value={ConsumationType.Passive}
                />
              </Stack>
            </RadioGroup>
          </Flex>
          <Flex direction='column' gap={8}>
            <Text>{translate('commons.choreographerInClub')}</Text>
            <RadioGroup
              value={JSON.stringify(form.getValues().choreographerPresence)}
              onChange={(value) => {
                form.setFieldValue(
                  'choreographerPresence',
                  JSON.parse(value) as boolean
                );
              }}
            >
              <Stack>
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.yes')}
                  value='true'
                />
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.no')}
                  value='false'
                />
              </Stack>
            </RadioGroup>
          </Flex>
          <Flex direction='column' gap={8}>
            <Text>{translate('commons.photoshootParticipation')}</Text>
            <RadioGroup
              value={JSON.stringify(form.getValues().photoshootMandatory)}
              onChange={(value) => {
                form.setFieldValue(
                  'photoshootMandatory',
                  JSON.parse(value) as boolean
                );
              }}
            >
              <Stack>
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.required')}
                  value='true'
                />
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.optional')}
                  value='false'
                />
              </Stack>
            </RadioGroup>
          </Flex>
          <Flex direction='column' gap={8}>
            <Text>{translate('commons.quantity')}</Text>
            <Stack gap={8}>
              <Flex align='center' gap={8} justify='space-between'>
                <Text>{translate('commons.artistsInShift')}</Text>
                <LBNumberInput
                  key={form.key('artistsPerShift')}
                  disabled={form.submitting}
                  error={form.validateField('artistsPerShift')}
                  maw={124}
                  placeholder='0'
                  {...form.getInputProps('artistsPerShift')}
                />
              </Flex>
              <Flex align='center' gap={8} justify='space-between'>
                <Text>{translate('commons.scenes')}</Text>
                <LBNumberInput
                  key={form.key('numberOfStages')}
                  disabled={form.submitting}
                  error={form.validateField('numberOfStages')}
                  maw={124}
                  placeholder='0'
                  {...form.getInputProps('numberOfStages')}
                />
              </Flex>
              <Flex align='center' gap={8} justify='space-between'>
                <Text>{translate('commons.dynamicPole')}</Text>
                <LBNumberInput
                  key={form.key('numberOfPolesDynamic')}
                  disabled={form.submitting}
                  error={form.validateField('numberOfPolesDynamic')}
                  maw={124}
                  placeholder='0'
                  {...form.getInputProps('numberOfPolesDynamic')}
                />
              </Flex>
              <Flex align='center' gap={8} justify='space-between'>
                <Text>{translate('commons.staticPole')}</Text>
                <LBNumberInput
                  key={form.key('numberOfPolesStatic')}
                  disabled={form.submitting}
                  error={form.validateField('numberOfPolesStatic')}
                  maw={124}
                  placeholder='0'
                  {...form.getInputProps('numberOfPolesStatic')}
                />
              </Flex>
            </Stack>
          </Flex>
          <Flex direction='column' gap={8}>
            <Text>
              {translate(
                'pages.onboarding.club.workConditions.form.noExperienceEmployment'
              )}
            </Text>
            <RadioGroup
              value={JSON.stringify(form.getValues().experienceRequired)}
              onChange={(value) => {
                form.setFieldValue(
                  'experienceRequired',
                  JSON.parse(value) as boolean
                );
              }}
            >
              <Stack>
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.yes')}
                  value='false'
                />
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.no')}
                  value='true'
                />
              </Stack>
            </RadioGroup>
          </Flex>
          <Flex direction='column' gap={8}>
            <Text>{translate('commons.artistTraining')}</Text>
            <RadioGroup
              value={JSON.stringify(form.getValues().artistTraining)}
              onChange={(value) => {
                form.setFieldValue(
                  'artistTraining',
                  JSON.parse(value) as boolean
                );
              }}
            >
              <Stack>
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.yes')}
                  value='true'
                />
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.no')}
                  value='false'
                />
              </Stack>
            </RadioGroup>
          </Flex>

          <Flex direction='column' gap={8}>
            <Text>{translate('commons.dressCodeForArtists')}</Text>
            <Flex gap={8} wrap='wrap'>
              {form.getValues().dressCodes.map((item, index) => (
                <LBBadge
                  key={item}
                  variant='outline'
                  rightSection={
                    <ActionIcon
                      disabled={form.submitting}
                      variant='transparent'
                      onClick={() => form.removeListItem('dressCodes', index)}
                    >
                      <XMarkIcon width={18} />
                    </ActionIcon>
                  }
                >
                  {item}
                </LBBadge>
              ))}
            </Flex>
            <LBLinkButton
              disabled={form.submitting}
              leftsection={<PlusCircleIcon width={18} />}
              onClick={open}
            >
              {translate('commons.add')}
            </LBLinkButton>
          </Flex>

          <Flex direction='column' gap={8}>
            <Text>
              {translate(
                'pages.onboarding.club.workConditions.form.additionalInfo.label'
              )}
            </Text>
            <LBTextArea
              key={form.key('additionalInformation')}
              disabled={form.submitting}
              {...form.getInputProps('additionalInformation')}
              autosize
              minRows={4}
              description={translate(
                'pages.onboarding.club.workConditions.form.additionalInfo.description'
              )}
              placeholder={formatMessage({
                id: 'pages.onboarding.club.workConditions.form.additionalInfo.placeholder'
              })}
            />
          </Flex>
        </Stack>
        <Space h={32} />
        <LBButton
          fullWidth
          disabled={!form.isValid()}
          loading={form.submitting}
          type='submit'
        >
          {translate('commons.finish')}
        </LBButton>
      </form>
    </>
  );
};
